import type { ReactNode } from 'react';

import type { ApprovalStatusBadgeVariant } from '../types';

import './ApprovalStatusBadge.css';

interface ApprovalStatusBadgeProps {
  variant: ApprovalStatusBadgeVariant;
  'data-testid'?: string;
  /** Rendered inside the pill, after the label — the FNMS retry control sits here. */
  children?: ReactNode;
}

const STATUS_LABELS: Record<ApprovalStatusBadgeVariant, string> = {
  // ACP-2752: "Save" rather than "Submission", and FNMS in capitals, per the HCD design —
  // it describes what actually failed and matches the wording of the alert above the table.
  'fnms-save-failed': 'FNMS Save Failed',
  'pending-dci-action': 'Pending: DCI Action',
  'pending-dci-review': 'Pending: DCI Review',
  'pending-federal-approver-review': 'Pending: Federal Approver Review',
};

const VARIANT_MODIFIERS: Record<ApprovalStatusBadgeVariant, string> = {
  'fnms-save-failed': 'approval-status-badge--fnms-save-failed',
  'pending-dci-action': 'approval-status-badge--pending',
  'pending-dci-review': 'approval-status-badge--pending',
  'pending-federal-approver-review': 'approval-status-badge--pending',
};

function ApprovalStatusBadge({ children, variant, 'data-testid': dataTestId }: ApprovalStatusBadgeProps) {
  const baseTestId = dataTestId ?? 'approval-status-badge';
  const displayText = STATUS_LABELS[variant];

  return (
    <span className={`approval-status-badge ${VARIANT_MODIFIERS[variant]}`}>
      {/* The live region covers the label only: an action rendered beside it is not status
          text, and announcing it as such would read the button out on every table update. */}
      <span
        aria-label={`Status: ${displayText}`}
        data-testid={baseTestId}
        role='status'>
        {displayText}
      </span>
      {children}
    </span>
  );
}

export default ApprovalStatusBadge;
